<template>
    <div
        v-if="isComponentReady"
        :class="wrapperClasses"
        class="l-ss-c-complex-popup"
        v-click-outside="clickOutside">
        <div
            v-if="showResults"
            v-show="isComplexResultVisible"
            class="l-ss-c-complex-search-results-wrapper"
            ref="resultswrapper"
            @click="closeCompletions">
            <search-results-wrapper
                :has-query="hasQuery"
                :has-results="hasResults"
                :store="store"
                :search-state="searchState"
                :query="query"
                :total-items-count="totalItemsCount"
                :is-results-visible="isResultsVisible"
                :is-filters-block-visible="isFiltersBlockVisible"
                :request-categories="requestCategories"
                :results-category-id="resultsCategoryId"
                :error-text-completions="errorTextCompletions"
                :error-text-search="errorTextSearch"
                :has-filters="hasFilters"
                :has-loaded-once="hasLoadedOnce"
                :is-completions-loading="isCompletionsLoading"
                :is-search-loading="isSearchLoading"
                :loading-by-filters="loadingByFilters"
                :limit="searchQueryLimit"
                :search-response="searchResponse"
                :search-request-id="searchRequestId"
                :skip="skip"
                :offer-templates="offerTemplates"
                :filter-items="filterItems"
                :sort-by="sortBy"
                :sort-dest="sortDest"
                :currency-in-offers="currencyInOffers"
                :filter-values="filterValues"
                :from_completions="from_completions"
                :search="search"
                :is-show-signature="isShowSignature"
                :adult-permission-confirmed="adultPermissionConfirmed"
                @autocomplete-search="val => $emit('autocomplete-search', val)"
                @params-search="params => $emit('params-search', params)"
                @navigate-category="handleNavigateCategory"
                @page-reload="handlePageReload"
                @search-image="$emit('search-image', $event)"
                @tags-click="$emit('tags-click', $event)"
                @handle-search="onSearchInputAction('handle-search')"
                @search-input-change="params => onSearchInputAction('search-input-change', params)"
                @offer-click="$emit('offer-click', $event)"
                @add-to-cart-click="$emit('add-to-cart-click', $event)"
                @add-to-favourites-click="$emit('add-to-favourites-click', $event)"
                @remove-from-favourites-click="$emit('remove-from-favourites-click', $event)"
                @age-confirmed="$emit('age-confirmed')"
                @results-category-click="$emit('results-category-click', $event)"
                @filters-change="filters => $emit('filters-change', filters)"
                @filter-click="params => $emit('filter-click', params)"
                @toggle-filters="toggleFilters"
                @sort-change="params => $emit('sort-change', params)"
                @result-sort-click="params => $emit('result-sort-click', params)"
                @reset-filters=" $emit('reset-filters', $event)"
                @navigate-page-next="() => $emit('navigate-page-next')"
                @navigate-page-prev="() => $emit('navigate-page-prev')"
                @button-image-click="event => $emit('button-image-click', { type: 'buttonImageClick', event })"/>
        </div>
        <div
            class="l-ss-c-completions-wrapper"
            :class="isCatalogCompletions ? 'l-ss-c-catalog-completions' : ''"
            ref="completionswrapper"
            v-show="isCompletionsVisible">
            <div class="l-ss-c-completions-wrapper_child">
                <completions-wrapper
                    ref="completions-wrapper"
                    :completions="completions"
                    :query="query"
                    :is-catalog-completions="isCatalogCompletions"
                    :taps-completions="tapsCompletions"
                    :is-completions-visible="isCompletionsVisible"
                    :is-show-signature="isShowSignature"
                    @click="handleClick"
                    @params-click="$emit('params-click', $event)"
                    @search-all="searchAll()"
                    @tags-click="handleTagsClick"
                    @add-to-cart-click="handleAddToCartClick"
                    @preview-tab-click="(value) => $emit('preview-tab-click', value)"
                    @preview-show-all-click="$emit('preview-show-all-click', $event)"
                    @show-all-click="handleShowAllClickEvent"
                    @reset-query="$emit('reset-query')"
                    @remove-history="(params) => $emit('remove-history', params)"/>
            </div>
        </div>
    </div>
</template>

<script>
    import { installPlugins } from '../core/plugins';
    import CompletionsMixin from '../core/mixins/completions';
    import RootComponentMixin from '../core/mixins/root-component';
    import CompletionsMethods from '../core/mixins/completions-methods';
    import CompletionsWrapper from '../core/components/completions/completions-wrapper';
    import SearchResultsMixin from '../core/mixins/search-results-mixin';
    import SearchResultsWrapper from '../core/components/search-results/search-results-wrapper';
    import ClickOutside from '../core/directives/clickOutside.js';
    import { DEFAULT_QUERY_LIMIT } from '../config';

    const EVENT_CALLBACK_DELAY = 300;
    const DEFAULT_QUERY_SKIP = 0;

    installPlugins();

    export default {
        components: {
            CompletionsWrapper,
            SearchResultsWrapper,
        },
        directives: {
            'click-outside': ClickOutside,
        },
        mixins: [
            CompletionsMethods,
            CompletionsMixin,
            SearchResultsMixin,
        ],
        provide: RootComponentMixin.provide,
        props: {
            ...RootComponentMixin.props,
            ...CompletionsMixin.props,
            search: {
                type: Object,
                required: false,
                default: () => null,
            },
            completions: {
                type: Array,
                default: () => ([]),
            },
            query: {
                type: String,
                default: '',
            },
            shouldInitCompletions: {
                type: Boolean,
                default: false,
            },
            hasQuery: {
                type: [String, Boolean],
                default: false,
            },
            requestCategories: {
                type: Object,
                required: false,
                default: () => null,
            },
            from_completions: {
                type: String,
                default: '',
            },
            requestSearch: {
                type: Object,
                required: false,
                default: () => null,
            },
            hasFilters: {
                type: [String, Boolean],
                default: false,
            },
            hasResults: {
                type: [String, Boolean],
                default: false,
            },
            hasLoadedOnce: {
                type: [String, Boolean],
                default: false,
            },
            limit: {
                type: [String, Number],
                default: DEFAULT_QUERY_LIMIT,
            },
            skip: {
                type: [String, Number],
                default: DEFAULT_QUERY_SKIP,
            },
            sortBy: {
                type: String,
                default: '',
            },
            sortDest: {
                type: String,
                default: '',
            },
            adultPermissionConfirmed: {
                type: Boolean,
                default: false,
            },
            isCompletionsVisible: {
                type: Boolean,
                default: false,
            },
            showResults: {
                type: Boolean,
                default: true,
            },
            isCatalogCompletions: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            ...RootComponentMixin.computed,
            ...SearchResultsMixin.computed,

            isComplexResultVisible() {
                return (this.hideResults === false &&
                    !this.showCompletions &&
                    (this.hasResults || this.requestSearch.isReady)) || this.base64Image || this.isSearchLoading;
            },
            searchQueryLimit() {
                return this.$configuration.searchQuery.limit || this.limit;
            },
            base64Image() {
                return this.store.state.base64Image;
            },
        },
        mounted() {
            this.$nextTick(() => {
                const completionsEl = this.$refs.completionswrapper;
                const resultsEl = this.$refs.resultswrapper;

                this.$emit('mounted', { completionsEl, resultsEl });
            });
        },
        methods: {
            ...RootComponentMixin.methods,

            clickOutside(event) {
                this.$emit('click-outside', event);
            },
            searchAll() {
                this.$emit('search-all');
            },
            closeCompletions(event) {
                if (this.$refs.resultswrapper.contains(event.target)) {
                    this.$emit('close-completions');
                }
            },
            handleClick(data) {
                this.store.commit('ui/SET_PROPERTY', { key: 'isCategorySearch', value: false });

                const handler = (completion) => {
                    this.$emit('click-item', {
                        ...completion, // TODO: deprecated [удалить c 01.03]
                        completion: completion,
                    });
                };

                data.currentQuery = this.query;
                data.isEmptyCompletions = this.requestCompletions.isEmpty;

                this.completionClickHandler(data, handler.bind(this));
            },
            // onTapAction(params) {
            //     this.$emit('search-input-change', params);
            //     // this.$emit('handle-search');
            // },
            handleTagsClick(data) {
                data.hasResults = this.isResultsVisible;
                this.onSearchInputAction('search-input-change', data);
                this.$emit('tags-click', data);
            },
            onSearchInputAction(eventName, params) {
                let afterEventCallback = null;
                switch (eventName) {
                    case 'search-input-change':
                        this.$emit(eventName, params);
                        break;
                    case 'handle-search':
                        this.toggleFilters(false);
                        this.$emit(eventName);
                        break;
                    case 'toggle-image-picker':
                        this.toggleImageSearch(params);
                        break;
                    case 'popup-close':
                        this.$emit('search-popup-close');
                        break;
                    case 'search-input-blur':
                        this.$emit('search-input-blur');
                        break;
                    case 'search-input-focus':
                        this.$emit('search-input-focus');
                        break;
                    case 'search-input-enter':
                        this.$emit(eventName, params);
                        break;
                    case 'reset-query':
                        this.base64Image = null;
                        this.$emit(eventName, params);
                        break;
                    case 'remove-history':
                        this.$emit('remove-history');
                        break;
                    default:
                        afterEventCallback = () => {
                            this.showSearch = true;
                        };
                        this.$emit(eventName, params);
                }
                this.store.commit('ui/SET_PROPERTY', { key: 'isCategoriesListVisible', value: false });
                if (afterEventCallback) {
                    setTimeout(afterEventCallback, EVENT_CALLBACK_DELAY);
                }
            },
        },
    };
</script>
